import {getValueByDevice, useDevice} from 'platform/foundation';
import {
  currencies,
  formatCurrencySymbol,
  useFormatCurrency,
  useFormatNumber,
  useFormatPercentage,
} from 'platform/locale';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Label,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  YAxisProps,
} from 'recharts';
import {CategoricalChartFunc} from 'recharts/types/chart/generateCategoricalChart';
import {useTheme, createGlobalStyle} from 'styled-components';
import {match} from 'ts-pattern';

import {useMemo, useState} from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';

import {isNil, length} from 'ramda';
import {isNotNil, isNumber} from 'ramda-adjunct';

import {CurrencyCodeType, RequiredTestIdProps, suffixTestId} from 'shared/web';

import i18n from '../../../../../i18n/i18n';
import {PriceReportType} from '../../../../../types/PriceReportType';
import {isInCypress} from '../../../../../utils/isInCypress';
import {MY_VEHICLE_COLOR} from '../consts/myVehicleColor';
import {useXAxisTickFormatter} from '../hooks/useXAxisTickFormatter';
import {
  Formatter,
  useYAxisTickFormatter,
  useYAxisTickFormatterWithUnitOnMobile,
} from '../hooks/useYAxisTickFormatter';
import {renderReferenceLineX} from '../renders/renderReferenceLineX';
import {renderReferenceLineY} from '../renders/renderReferenceLineY';
import {ChartVariant} from '../types/ChartVariant';
import {ExtendedStatisticsCar} from '../types/ExtendedStatisticsCar';
import {StatisticsChartItem} from '../types/StatisticsChartItem';
import {getAverageValueWithoutMyVehicle} from '../utils/getAverageValueWithoutMyVehicle';
import {getChartCoordinates} from '../utils/getChartCoordinates';
import {getXAxisLabel} from '../utils/getXAxisLabel';
import {getYAxisLabel} from '../utils/getYAxisLabel';
import {getYsForStackedBar} from '../utils/getYsForStackedBar';
import {sortStatisticsCarsForChart} from '../utils/sortStatisticsCarsForChart';
import {BarShape} from './BarShape';
import {ChartTooltip} from './ChartTooltip';
import {MyAdLabel} from './MyAdLabel';
import {ScatterShape} from './ScatterShape';

const AXIS_OFFSET = 100;
const ROUND_TO_VALUE = 100;

const axisDomain: YAxisProps['domain'] = [
  (minValue: number) =>
    Math.max(0, Math.floor((minValue - AXIS_OFFSET) / ROUND_TO_VALUE) * ROUND_TO_VALUE),
  'auto',
];

// CSS styles for font consistency
const chartStyles = {
  fontFamily: 'Roboto, sans-serif',
  WebkitFontSmoothing: 'antialiased' as const,
  MozOsxFontSmoothing: 'grayscale' as const,
  textRendering: 'optimizeLegibility' as const,
};

// Global styles for chart SVG elements
// eslint-disable-next-line eag/no-css-property
const ChartGlobalStyles = createGlobalStyle`
  .recharts-surface {
    overflow: visible !important;
  }
`;

interface ChartViewProps extends RequiredTestIdProps {
  chartVariant: ChartVariant;
  priceReport: PriceReportType;
  data: ExtendedStatisticsCar[];
  selectedVehicleIds: string[] | null;
  setSelectedVehicleIds: (ids: string[] | null) => void;
}

export function ChartView(props: ChartViewProps) {
  const theme = useTheme();
  const formatPercentage = useFormatPercentage();
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();
  const device = useDevice();
  const [activeTooltipIndex, setActiveTooltipIndex] = useState<number | null>(null);

  const isScatterChart = props.chartVariant === 'priceMap';

  const onMouseMoveBarChart: CategoricalChartFunc = (state) => {
    if (isNil(state)) {
      return;
    }
    if (!state.isTooltipActive) {
      setActiveTooltipIndex(null);
    }
    if (state.activePayload?.[0]?.payload?.isMyVehicle) {
      return;
    }
    if (isNumber(state.activeTooltipIndex)) {
      setActiveTooltipIndex(state.activeTooltipIndex);
    }
  };

  const onClick = (item: StatisticsChartItem) =>
    props.setSelectedVehicleIds(
      props.selectedVehicleIds?.includes(item.car.ad_id) ? null : [item.car.ad_id]
    );

  const currencyCode = props.priceReport.sellingPrice.recommended.currency as CurrencyCodeType;
  const currencySymbol = formatCurrencySymbol(currencies, currencyCode);

  const xAxisTickFormatter = useXAxisTickFormatter(props.chartVariant);
  const yAxisTickFormatter = useYAxisTickFormatter(props.chartVariant);
  const yAxisTickFormatterWithUnitOnMobile = useYAxisTickFormatterWithUnitOnMobile(
    props.chartVariant
  );

  const sortedData = useMemo(
    () => sortStatisticsCarsForChart(props.data, props.chartVariant),
    [props.data, props.chartVariant]
  );

  // mapped data must be memoized - when mapped data is changed, recharts reset current zoom to default
  const mappedData: StatisticsChartItem[] = useMemo(() => {
    const myVehicle = sortedData.find((car) => car.isMyVehicle);

    return sortedData.map((car, index) => {
      const coordinates = getChartCoordinates(car, props.chartVariant);
      const ysForStackedBar = getYsForStackedBar(car, myVehicle ?? car, props.chartVariant);

      return {
        index,
        car,
        ...coordinates,
        ...ysForStackedBar,
      };
    });
  }, [sortedData, props.chartVariant]);

  const averageValueX = isScatterChart ? getAverageValueWithoutMyVehicle('x', mappedData) : null;
  const averageValueY = getAverageValueWithoutMyVehicle('y', mappedData);

  const myVehicleItem = mappedData.find((item) => item.car.isMyVehicle);

  const Chart = isScatterChart ? ScatterChart : BarChart;
  const isAnimationActive = !isInCypress;

  const yReferenceLineLabelFormatter: Formatter = (value) =>
    match<ChartVariant, string>(props.chartVariant)
      .with('price', () =>
        i18n.t('entity.statisticsChart.labels.thousandsCurrency', {
          value: yAxisTickFormatter(value),
          currencySymbol,
        })
      )
      .with('priceMap', () =>
        i18n.t('entity.statisticsChart.labels.thousandsCurrency', {
          value: yAxisTickFormatter(value),
          currencySymbol,
        })
      )
      .with('mileage', () =>
        i18n.t('entity.statisticsChart.labels.thousandsKm', {
          value: yAxisTickFormatter(value),
        })
      )
      .with('featuresLevel', () => formatPercentage(value / 100, 'down'))
      .otherwise(() => yAxisTickFormatter(value));

  const myAdLabelFormatter: Formatter = (value) =>
    match<ChartVariant, string>(props.chartVariant)
      .with(
        'price',
        () => formatCurrency(value, props.priceReport.sellingPrice.recommended.currency) ?? '?'
      )
      .with('mileage', () => `${formatNumber(value) ?? '?'} ${i18n.t('general.metric.km')}`)
      .with('featuresLevel', () => formatPercentage(value / 100, 'down'))
      .otherwise(() => formatNumber(value) ?? '?');

  return (
    <>
      <ChartGlobalStyles />
      <AutoSizer>
        {({width, height}: {width: number; height: number}) => (
          <Chart
            data={mappedData}
            onMouseMove={!isScatterChart ? onMouseMoveBarChart : undefined}
            width={width}
            height={height}
            margin={{top: 0, right: 0, bottom: 0, left: 0}}
            // eslint-disable-next-line react/forbid-component-props
            style={chartStyles}
          >
            <CartesianGrid stroke={theme.colors.palettes.neutral['30'][100]} />
            {isScatterChart ? (
              <Scatter
                data={mappedData}
                onClick={onClick}
                shape={
                  <ScatterShape
                    priceReport={props.priceReport}
                    selectedVehicleIds={props.selectedVehicleIds}
                    activeTooltipIndex={activeTooltipIndex}
                  />
                }
                isAnimationActive={isAnimationActive}
              />
            ) : (
              <>
                <Bar
                  dataKey="y1"
                  stackId="a"
                  minPointSize={5}
                  onClick={onClick}
                  shape={
                    <BarShape
                      priceReport={props.priceReport}
                      selectedVehicleIds={props.selectedVehicleIds}
                      activeTooltipIndex={activeTooltipIndex}
                    />
                  }
                  label={
                    <MyAdLabel
                      data={mappedData}
                      formatter={myAdLabelFormatter}
                      comparison="positive"
                      chartWidth={width}
                    />
                  }
                  isAnimationActive={isAnimationActive}
                />
                <Bar
                  dataKey="y2"
                  stackId="a"
                  onClick={onClick}
                  shape={
                    <BarShape
                      priceReport={props.priceReport}
                      selectedVehicleIds={props.selectedVehicleIds}
                      activeTooltipIndex={activeTooltipIndex}
                    />
                  }
                  label={
                    <MyAdLabel
                      data={mappedData}
                      formatter={myAdLabelFormatter}
                      comparison="negative"
                      chartWidth={width}
                    />
                  }
                  isAnimationActive={isAnimationActive}
                />
              </>
            )}
            <YAxis
              dataKey="y"
              type="number"
              tickFormatter={yAxisTickFormatterWithUnitOnMobile}
              tickLine={false}
              fontSize={12}
              axisLine={false}
              width={getValueByDevice(device, 48, 72, 72, 72)}
              padding={{top: 40}}
              domain={axisDomain}
            >
              <Label
                value={getYAxisLabel(props.chartVariant, currencyCode, device)}
                angle={-90}
                position="insideLeft"
                fontSize={14}
              />
            </YAxis>
            <XAxis
              dataKey="x"
              type={isScatterChart ? 'number' : 'category'}
              axisLine={false}
              tickFormatter={xAxisTickFormatter}
              tickLine={false}
              fontSize={12}
              height={48}
              domain={isScatterChart ? axisDomain : undefined}
              padding={isScatterChart ? {right: 40} : undefined}
            >
              <Label
                value={getXAxisLabel(props.chartVariant)}
                position="insideBottom"
                fontSize={14}
              />
            </XAxis>
            {device !== 'mobile' && (
              <Tooltip
                content={
                  <ChartTooltip
                    priceReport={props.priceReport}
                    data-testid={suffixTestId('chartTooltip', props)}
                  />
                }
                wrapperStyle={{outline: 'none'}}
                cursor={false}
              />
            )}
            {myVehicleItem && (
              <>
                {renderReferenceLineX({
                  value: myVehicleItem.x,
                  formatter: xAxisTickFormatter,
                  flagColor: 'black',
                  flagVerticalAlign: 'bottom',
                  flagHorizontalAlign: 'left',
                  lineColor: MY_VEHICLE_COLOR,
                  isLabelVisible: props.chartVariant === 'priceMap',
                })}
                {renderReferenceLineY({
                  value: myVehicleItem.y,
                  formatter: yReferenceLineLabelFormatter,
                  flagColor: 'black',
                  flagVerticalAlign: 'bottom',
                  lineColor: MY_VEHICLE_COLOR,
                })}
              </>
            )}
            {length(mappedData) > 1 && (
              <>
                {renderReferenceLineY({
                  value: averageValueY,
                  formatter: (value) => `Ø ${yReferenceLineLabelFormatter(value)}`,
                  lineColor: theme.colors.palettes.neutral[60][100],
                  flagColor: 'neutral',
                  flagVerticalAlign: 'bottom',
                  flagHorizontalAlign: 'right',
                  isFlagSubtle: true,
                  isDashed: true,
                })}
                {isNotNil(averageValueX) &&
                  renderReferenceLineX({
                    value: averageValueX,
                    formatter: (value) => `Ø ${xAxisTickFormatter(value)}`,
                    lineColor: theme.colors.palettes.neutral[60][100],
                    flagColor: 'neutral',
                    flagHorizontalAlign: 'right',
                    isFlagSubtle: true,
                    isDashed: true,
                    isLabelVisible: props.chartVariant === 'priceMap',
                  })}
              </>
            )}
          </Chart>
        )}
      </AutoSizer>
    </>
  );
}
